import React from 'react';
import { Helmet } from 'react-helmet-async';
import './Terms.css';

function Terms() {
  return (
    <div className="terms-page">
      <Helmet>
        <title>Terms of Service - Audra Books</title>
        <meta name="description" content="Terms of Service for Audra Books, a polished Audiobookshelf client for iOS and Android. Read our usage terms, subscription policies, and legal information." />
        <link rel="canonical" href="https://audrabooks.app/terms" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="container">
        <div className="terms-content">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: December 4, 2025</p>

          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              By downloading, installing, or using Audra Books ("the App"), you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree to these Terms, do not use the App.
            </p>
            <p>
              Audra Books is developed and operated by Eric Randall ("we," "us," or "our"). These Terms
              govern your use of the Audra Books application on iOS and Android devices.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              Audra Books is a mobile application that serves as a client for Audiobookshelf servers.
              The App provides:
            </p>
            <ul>
              <li>Audiobook streaming and playback from your personal Audiobookshelf server</li>
              <li>Ebook reading for EPUB and PDF files stored on your server</li>
              <li>Download capabilities for offline listening and reading</li>
              <li>Progress synchronization across devices</li>
              <li>Personal listening statistics and tracking</li>
              <li>Library organization and browsing features</li>
            </ul>
            <p>
              The App requires a connection to an Audiobookshelf server that you own, operate, or have
              authorized access to. We do not provide, host, or manage Audiobookshelf servers.
            </p>
          </section>

          <section>
            <h2>3. License Grant</h2>
            <p>
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
              non-transferable, revocable license to download, install, and use the App for your
              personal, non-commercial use on devices that you own or control.
            </p>
          </section>

          <section>
            <h2>4. Account and Server Requirements</h2>
            <p>To use Audra Books, you must:</p>
            <ul>
              <li>Have access to a valid Audiobookshelf server with proper credentials</li>
              <li>Be authorized to access the content on your connected server</li>
              <li>Maintain the security and confidentiality of your server credentials</li>
              <li>Accept responsibility for all activities that occur through your use of the App</li>
            </ul>
            <p>
              You are solely responsible for ensuring that your use of any Audiobookshelf server
              complies with all applicable laws and the terms of service of that server.
            </p>
          </section>

          <section>
            <h2>5. Subscriptions and Payments</h2>
            <p>
              Audra Books offers both free and premium ("Pro") features. Pro features are available
              through auto-renewing subscription plans.
            </p>
            <ul>
              <li>Subscriptions are billed to your Apple ID or Google Play account</li>
              <li>Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period</li>
              <li>You can manage and cancel subscriptions through your device's app store settings</li>
              <li>No refunds are provided for unused portions of subscription periods</li>
              <li>Prices may vary by region and are subject to change with notice</li>
            </ul>
          </section>

          <section>
            <h2>6. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Reverse engineer, decompile, or disassemble the App</li>
              <li>Modify, adapt, or create derivative works based on the App</li>
              <li>Access servers or content without proper authorization</li>
              <li>Use the App to distribute copyrighted material illegally</li>
              <li>Use automated systems or scripts to interact with the App</li>
              <li>Attempt to circumvent any security features of the App</li>
              <li>Use the App for any unlawful purpose</li>
            </ul>
          </section>

          <section>
            <h2>7. Intellectual Property</h2>
            <p>
              The App, including its design, features, code, graphics, and documentation, is owned by
              Eric Randall and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              "Audra Books," "Audra," and associated logos are trademarks of Eric Randall. You may not
              use these trademarks without prior written permission.
            </p>
            <p>
              Audiobookshelf is a separate open-source project and is not affiliated with Audra Books.
              All Audiobookshelf trademarks belong to their respective owners.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>
              The App connects to third-party Audiobookshelf servers that are not operated by us.
              We are not responsible for:
            </p>
            <ul>
              <li>The availability, security, or reliability of any Audiobookshelf server</li>
              <li>The content stored on or transmitted through any server</li>
              <li>Data loss or corruption on third-party servers</li>
              <li>The terms of service or privacy practices of server operators</li>
            </ul>
          </section>

          <section>
            <h2>9. Disclaimer of Warranties</h2>
            <p>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the App will be uninterrupted, error-free, secure, or compatible
              with all devices or operating system versions.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL ERIC RANDALL BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS
              OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL,
              OR OTHER INTANGIBLE LOSSES.
            </p>
            <p>
              Our total liability for any claims arising from or related to these Terms or your use of
              the App shall not exceed the amounts you have paid to us in the twelve (12) months
              preceding the claim.
            </p>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Eric Randall from any claims, damages, losses,
              or expenses (including reasonable attorneys' fees) arising from your use of the App,
              your violation of these Terms, or your violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of significant
              changes by updating the "Last Updated" date and, where appropriate, through the App or
              other communication channels.
            </p>
            <p>
              Your continued use of the App after changes become effective constitutes your acceptance
              of the revised Terms.
            </p>
          </section>

          <section>
            <h2>13. Termination</h2>
            <p>
              We may terminate or suspend your access to the App at any time, without prior notice,
              for conduct that we believe violates these Terms or is harmful to other users, us, or
              third parties, or for any other reason at our sole discretion.
            </p>
          </section>

          <section>
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State
              of Utah, United States, without regard to its conflict of law provisions. Any disputes
              arising from these Terms shall be resolved in the courts of Cache County, Utah.
            </p>
          </section>

          <section>
            <h2>15. Contact Us</h2>
            <p>
              If you have any questions, complaints, or claims with respect to the Licensed Application
              or these Terms, please contact us:
            </p>
            <p className="contact-info">
              Email: <a href="mailto:hello@audrabooks.app">hello@audrabooks.app</a><br />
              Address: 25 S 200 W, Hyde Park, UT 84318
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Terms;
